import { useState } from 'react'
import { Dialog, DialogBody } from '@/components/Dialog'
import { Button } from '@/UIComponents2/Button'
import { Checkbox } from '@/UIComponents2/Checkbox'
import { TextField } from '@/UIComponents2/TextField'
import { AccentBadge } from '@/components/AccentBadge'
import { cn } from '@/utlis/cn'
import { useTranslation } from 'react-i18next'
import DialogActionButtonsSection from '@/dialogs/components/DialogActionButtonsSection'

export type SessionFeedbackExitModes = 'send-feedback' | 'click-x'

export type SessionFeedbackResult = {
    ratingValue: number
    didModifyValue: boolean
    feedbackText: string
    allowContact: boolean | null
    exitMode: SessionFeedbackExitModes
}

type SessionFeedbackPopupProps = {
    open: boolean
    sessionId: string
    onClose: (feedback: SessionFeedbackResult) => void
}
export function SessionFeedbackPopup(props: SessionFeedbackPopupProps) {
    const { t } = useTranslation()
    const [rating, setRating] = useState(0)
    const [didModify, setDidModify] = useState(false)
    const [feedbackText, setFeedbackText] = useState('')
    const [allowContact, setAllowContact] = useState(true)

    const sendSessionFeedback = async (exitMode: SessionFeedbackExitModes) => {
        props.onClose({
            ratingValue: rating,
            didModifyValue: didModify,
            feedbackText: feedbackText,
            allowContact: allowContact,
            exitMode: exitMode,
        })
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.ctrlKey && event.key === 'Enter') {
            sendSessionFeedback('send-feedback')
            event.preventDefault()
        }
    }

    return (
        <Dialog title={t('Session Feedback')} open={props.open} onClose={() => sendSessionFeedback('click-x')}>
            <DialogBody className="flex flex-col gap-[10px]">
                <AccentBadge>
                    <ChatIcon />
                </AccentBadge>
                <h2 className="t-title-dialog pt-2 text-center">{t('How Was Your Session?')}</h2>
                <div>
                    <Rating
                        value={rating}
                        onChange={(v) => {
                            setRating(v)
                            setDidModify(true)
                        }}
                    />
                    <div className="mt-2 w-full h-[1.5px] bg-white/5"></div>
                </div>
                <div className="flex flex-col gap-2">
                    <div className="text-base-medium">{t('Let us know how we can improve')}</div>
                    <TextField
                        placeholder={t('Write how your session went...')}
                        onChange={(v) => {
                            setFeedbackText(v)
                            setDidModify(true)
                        }}
                        onKeyDown={handleKeyDown}
                    />
                </div>
                <Checkbox
                    label={t('Allow us to contact you about this feedback')}
                    checked={allowContact}
                    onChange={(v) => {
                        setAllowContact(v)
                        setDidModify(true)
                    }}
                />
            </DialogBody>
            <DialogActionButtonsSection
                rightButtonText={t('Send Feedback')}
                onRightButtonClicked={() => sendSessionFeedback('send-feedback')}
            />
        </Dialog>
    )
}

type RatingProps = {
    value: number
    onChange: (value: number) => void
}
function Rating(props: RatingProps) {
    const { t } = useTranslation()
    const [hoverValue, setHoverValue] = useState(0)

    const renderStar = (n: number, label: string | null) => {
        return (
            <div
                className={cn(
                    'flex flex-col items-center justify-start w-full gap-2',
                    hoverValue >= n ? 'text-muse-fg/40' : 'text-muse-fg/20'
                )}
            >
                <Button
                    className="w-full flex justify-center"
                    onClick={() => props.onChange(n)}
                    onMouseEnter={() => setHoverValue(n)}
                    onMouseLeave={() => setHoverValue(0)}
                >
                    <Star filled={props.value >= n} />
                </Button>
                {label ?? <span className="t-sm-light">{label}</span>}
            </div>
        )
    }

    return (
        <div className="flex justify-center">
            <div className="flex justify-between w-[220px]">
                {renderStar(1, null)}
                {renderStar(2, null)}
                {renderStar(3, null)}
                {renderStar(4, null)}
                {renderStar(5, null)}
            </div>
        </div>
    )
}

type StarProps = {
    filled?: boolean
}
function Star(props: StarProps) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 36 35" fill="none">
            <g>
                <path
                    d="M17.0489 0.927052C17.3483 0.0057416 18.6517 0.00574017 18.9511 0.927051L22.4903 11.8197C22.6242 12.2317 23.0081 12.5106 23.4413 12.5106H34.8945C35.8632 12.5106 36.266 13.7503 35.4823 14.3197L26.2165 21.0517C25.866 21.3063 25.7193 21.7577 25.8532 22.1697L29.3924 33.0623C29.6918 33.9836 28.6373 34.7497 27.8536 34.1803L18.5878 27.4483C18.2373 27.1937 17.7627 27.1937 17.4122 27.4483L8.14641 34.1803C7.36269 34.7497 6.30821 33.9836 6.60757 33.0623L10.1468 22.1697C10.2807 21.7577 10.134 21.3063 9.78352 21.0517L0.517712 14.3197C-0.266001 13.7503 0.136773 12.5106 1.1055 12.5106H12.5587C12.9919 12.5106 13.3758 12.2317 13.5097 11.8197L17.0489 0.927052Z"
                    fill={props.filled ? 'url(#paint0_radial_1093_23239)' : 'currentColor'}
                />
                <path
                    d="M17.5244 1.08203C17.6648 0.650113 18.246 0.622953 18.4414 1.00098L18.4756 1.08203L22.0146 11.9746C22.2156 12.5924 22.7917 13.0107 23.4414 13.0107H34.8945C35.3487 13.0108 35.5542 13.5558 35.2549 13.8584L35.1885 13.915L25.9229 20.6475C25.4299 21.0056 25.2052 21.6229 25.3447 22.208L25.3779 22.3242L28.917 33.2168C29.0573 33.6486 28.6029 34.0119 28.2227 33.8213L28.1475 33.7754L18.8818 27.0439C18.3889 26.6858 17.7321 26.6632 17.2188 26.9766L17.1182 27.0439L7.85254 33.7754C7.48508 34.0424 6.99823 33.7225 7.0625 33.3018L7.08301 33.2168L10.6221 22.3242C10.8103 21.7448 10.6294 21.1131 10.1729 20.7217L10.0771 20.6475L0.811523 13.915C0.419817 13.6303 0.621173 13.0108 1.10547 13.0107H12.5586C13.1678 13.0107 13.7122 12.643 13.9434 12.0879L13.9854 11.9746L17.5244 1.08203Z"
                    stroke="url(#paint1_linear_1093_23239)"
                    strokeOpacity="0.2"
                />
            </g>
            <defs>
                <clipPath id="bgblur_0_1093_23239_clip_path" transform="translate(14.4538 14.321)">
                    <path d="M17.0489 0.927052C17.3483 0.0057416 18.6517 0.00574017 18.9511 0.927051L22.4903 11.8197C22.6242 12.2317 23.0081 12.5106 23.4413 12.5106H34.8945C35.8632 12.5106 36.266 13.7503 35.4823 14.3197L26.2165 21.0517C25.866 21.3063 25.7193 21.7577 25.8532 22.1697L29.3924 33.0623C29.6918 33.9836 28.6373 34.7497 27.8536 34.1803L18.5878 27.4483C18.2373 27.1937 17.7627 27.1937 17.4122 27.4483L8.14641 34.1803C7.36269 34.7497 6.30821 33.9836 6.60757 33.0623L10.1468 22.1697C10.2807 21.7577 10.134 21.3063 9.78352 21.0517L0.517712 14.3197C-0.266001 13.7503 0.136773 12.5106 1.1055 12.5106H12.5587C12.9919 12.5106 13.3758 12.2317 13.5097 11.8197L17.0489 0.927052Z" />
                </clipPath>
                <radialGradient
                    id="paint0_radial_1093_23239"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(-2.82203 2.03846) rotate(47.375) scale(62.0203 180.039)"
                >
                    <stop stopColor="#FCE640" />
                    <stop offset="0.770739" stopColor="#F49629" />
                    <stop offset="1" stopColor="#F17E22" />
                </radialGradient>
                <linearGradient
                    id="paint1_linear_1093_23239"
                    x1="18"
                    y1="-2"
                    x2="18"
                    y2="40"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="white" />
                    <stop offset="1" stopColor="white" stopOpacity="0.05" />
                </linearGradient>
            </defs>
        </svg>
    )
}

function ChatIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 36 36" fill="none">
            <g>
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M23.9256 20.1244C23.0976 20.1244 22.4181 19.4539 22.4181 18.6244C22.4181 17.7949 23.0826 17.1244 23.9121 17.1244H23.9256C24.7536 17.1244 25.4256 17.7949 25.4256 18.6244C25.4256 19.4539 24.7536 20.1244 23.9256 20.1244ZM17.9121 20.1244C17.0826 20.1244 16.4046 19.4539 16.4046 18.6244C16.4046 17.7949 17.0706 17.1244 17.8986 17.1244H17.9121C18.7401 17.1244 19.4121 17.7949 19.4121 18.6244C19.4121 19.4539 18.7401 20.1244 17.9121 20.1244ZM11.8986 20.1244C11.0706 20.1244 10.3926 19.4539 10.3926 18.6244C10.3926 17.7949 11.0556 17.1244 11.8851 17.1244H11.8986C12.7266 17.1244 13.3986 17.7949 13.3986 18.6244C13.3986 19.4539 12.7266 20.1244 11.8986 20.1244ZM29.1396 6.86438C26.1681 3.89288 22.2126 2.25488 18.0036 2.25488C13.7946 2.25488 9.83909 3.89288 6.86759 6.86438C2.68703 11.0463 1.23817 17.237 2.97249 22.7309C3.3657 23.9765 3.67502 25.2225 3.50309 26.5174C3.14009 29.2264 2.95409 31.2154 3.87359 32.1364C4.79159 33.0559 6.77909 32.8699 9.48809 32.5054C10.73 32.3405 11.9363 32.6009 13.1284 32.986C14.7093 33.4966 16.3435 33.7459 17.9691 33.7459C22.0761 33.7459 26.1291 32.1529 29.1396 29.1409C35.2791 22.9984 35.2806 13.0054 29.1396 6.86438Z"
                    fill="url(#paint0_radial_1093_23337)"
                />
                <path
                    d="M18.0034 2.75488C21.9525 2.75488 25.6686 4.243 28.5132 6.95215L28.7856 7.21777C34.6384 13.0705 34.7288 22.5385 29.0601 28.5059L28.7856 28.7871C25.8721 31.7021 21.948 33.2461 17.9692 33.2461C16.5915 33.2461 15.2075 33.061 13.8579 32.6836L13.2817 32.5098C12.1406 32.1412 10.935 31.8666 9.67529 31.9814L9.42236 32.0098H9.42139C8.06145 32.1927 6.92554 32.3245 6.021 32.3027C5.21813 32.2834 4.69017 32.1434 4.35791 31.8955L4.22705 31.7832C3.90831 31.4638 3.72979 30.9033 3.70752 29.9854C3.69105 29.3063 3.76079 28.4973 3.87354 27.5615L3.99854 26.584V26.583C4.16246 25.3482 3.92237 24.1708 3.59424 23.0547L3.44971 22.5801C1.82364 17.4289 3.08889 11.6412 6.84912 7.60352L7.22119 7.21777C10.0988 4.34025 13.927 2.75493 18.0034 2.75488ZM11.8853 16.624C10.7765 16.624 9.89326 17.5218 9.89307 18.624C9.89307 19.7326 10.7977 20.624 11.8989 20.624C13.0026 20.6238 13.8989 19.7299 13.8989 18.624C13.8987 17.5183 13.0025 16.6242 11.8989 16.624H11.8853ZM17.8989 16.624C16.7926 16.624 15.905 17.5208 15.9048 18.624C15.9048 19.7328 16.8094 20.6238 17.9116 20.624C19.0154 20.624 19.9116 19.73 19.9116 18.624C19.9114 17.5182 19.0153 16.624 17.9116 16.624H17.8989ZM23.9116 16.624C22.8037 16.6243 21.9186 17.5213 21.9185 18.624C21.9185 19.7331 22.8249 20.6239 23.9253 20.624C25.0291 20.624 25.9253 19.73 25.9253 18.624C25.9251 17.5182 25.029 16.624 23.9253 16.624H23.9116Z"
                    stroke="url(#paint1_linear_1093_23337)"
                    strokeOpacity="0.2"
                />
            </g>
            <defs>
                <clipPath id="bgblur_0_1093_23337_clip_path" transform="translate(12.303 12.3025)">
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M23.9256 20.1244C23.0976 20.1244 22.4181 19.4539 22.4181 18.6244C22.4181 17.7949 23.0826 17.1244 23.9121 17.1244H23.9256C24.7536 17.1244 25.4256 17.7949 25.4256 18.6244C25.4256 19.4539 24.7536 20.1244 23.9256 20.1244ZM17.9121 20.1244C17.0826 20.1244 16.4046 19.4539 16.4046 18.6244C16.4046 17.7949 17.0706 17.1244 17.8986 17.1244H17.9121C18.7401 17.1244 19.4121 17.7949 19.4121 18.6244C19.4121 19.4539 18.7401 20.1244 17.9121 20.1244ZM11.8986 20.1244C11.0706 20.1244 10.3926 19.4539 10.3926 18.6244C10.3926 17.7949 11.0556 17.1244 11.8851 17.1244H11.8986C12.7266 17.1244 13.3986 17.7949 13.3986 18.6244C13.3986 19.4539 12.7266 20.1244 11.8986 20.1244ZM29.1396 6.86438C26.1681 3.89288 22.2126 2.25488 18.0036 2.25488C13.7946 2.25488 9.83909 3.89288 6.86759 6.86438C2.68703 11.0463 1.23817 17.237 2.97249 22.7309C3.3657 23.9765 3.67502 25.2225 3.50309 26.5174C3.14009 29.2264 2.95409 31.2154 3.87359 32.1364C4.79159 33.0559 6.77909 32.8699 9.48809 32.5054C10.73 32.3405 11.9363 32.6009 13.1284 32.986C14.7093 33.4966 16.3435 33.7459 17.9691 33.7459C22.0761 33.7459 26.1291 32.1529 29.1396 29.1409C35.2791 22.9984 35.2806 13.0054 29.1396 6.86438Z"
                    />
                </clipPath>
                <radialGradient
                    id="paint0_radial_1093_23337"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(2.38783 5.28286) rotate(47.3755) scale(46.5015 134.989)"
                >
                    <stop stopColor="#FCE640" />
                    <stop offset="0.770739" stopColor="#F49629" />
                    <stop offset="1" stopColor="#F17E22" />
                </radialGradient>
                <linearGradient
                    id="paint1_linear_1093_23337"
                    x1="17.9996"
                    y1="2.25488"
                    x2="17.9996"
                    y2="33.7459"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="white" />
                    <stop offset="1" stopColor="white" stopOpacity="0.05" />
                </linearGradient>
            </defs>
        </svg>
    )
}
